import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { ENDRE, LAGRET, lagreValg, lesValg, type Valg } from '../lib/informasjonskapsler';
import {
  glemBesoksId,
  glemLagretKampanje,
  huskKampanje,
  meldEtterSamtykke,
  sendVentendeSlettinger,
} from '../lib/trakt';

/**
 * Beskjeden om informasjonskapsler, og de to valgene som finnes.
 *
 * Kapslene Vår Dag setter, er alle nødvendige for at tjenesten skal virke:
 * innloggingen din, innloggingen til administrasjonsportalen, og beviset på at
 * du kom inn bak passordet mens siden bygges. For slike kreves det etter
 * ekomloven § 3-15 informasjon, ikke samtykke.
 *
 * To ting er noe annet, og det er dem boksen spør om. Annonsemålingen: kommer
 * noen inn på en annonselenke, bærer den med seg en klikk-ID som finnes for å
 * meldes tilbake til annonsenettverket som en konvertering. Og
 * besøksstatistikken: en tilfeldig ID som lar oss kjenne igjen nettleseren fra
 * besøk til besøk. Begge krever et ja.
 *
 * Derfor har boksen to knapper, og begge betyr noe. De er like store med
 * vilje: et «nei» som er vanskeligere å trykke på enn et «ja», er ikke et
 * fritt valg, og et samtykke som ikke er fritt, er ikke et samtykke. «Velg
 * selv» gir ett valg per formål, for den som vil si ja til det ene og ikke det
 * andre – et samtykke skal kunne gis til hvert formål for seg.
 *
 * Personvernsida kan be boksen komme fram igjen (`aapneValget`). Det er veien
 * til å trekke samtykket tilbake for den som ikke har konto.
 *
 * Er noen logget inn, gjelder valget om annonsemåling også kontoen. Samtykket
 * til klikk-ID-en står to steder – i nettleseren og på kontoen – og et nei her
 * som lot kontoen beholde ja-et og klikk-ID-en, ville vært et nei som ikke
 * virket. Besøksstatistikken står bare i nettleseren.
 *
 * Den ligger nederst og skal ikke dekke noe man må trykke på. Høyden måles og
 * settes som `--kapselvarsel-hoyde`, så sidene som fyller hele vinduet kan
 * krympe tilsvarende mens beskjeden står.
 */
export function Kapselvarsel() {
  /**
   * `null` betyr «vet ikke ennå».
   *
   * Lagringen leses i en effekt, ikke under første rendring: på en side som en
   * dag kan bygges på serveren finnes ikke `localStorage` der, og en boks som
   * blinker til og forsvinner er verre enn en som kommer et øyeblikk for sent.
   */
  const [vises, setVises] = useState(false);
  /** Om valgene for hvert formål vises, i stedet for de to knappene alene. */
  const [velger, setVelger] = useState(false);
  const [valgt, setValgt] = useState<Valg>({ annonsemaaling: false, statistikk: false });

  const { status, user, refresh } = useAuth();
  // Hendelsene under lyttes på én gang, og trenger kontoen slik den er når de
  // kommer – ikke slik den var da boksen ble tegnet første gang.
  const konto = useRef<{ id: string; annonsemaaling: boolean } | null>(null);
  konto.current =
    status === 'authenticated' && user ? { id: user.id, annonsemaaling: user.annonsemaaling } : null;

  // En sletting som ikke kom fram sist noen sa nei, prøves igjen når økta er
  // kjent. Boksen ligger på hver side, så det skjer ved neste besøk uansett
  // hvor – og en kontosletting venter til den samme kontoen er logget inn.
  const innlogget = status === 'authenticated' ? (user?.id ?? null) : null;
  useEffect(() => {
    if (status !== 'loading') sendVentendeSlettinger(innlogget);
  }, [status, innlogget]);

  useEffect(() => {
    setVises(lesValg() === null);

    // Bedt om å komme fram igjen: med valget som står, og med ett valg per
    // formål, siden det er det den som spør vil endre.
    const endre = () => {
      const naa = lesValg();
      setValgt({
        // Kontoen sier hva som faktisk gjelder for den som er logget inn.
        annonsemaaling: konto.current?.annonsemaaling ?? naa?.annonsemaaling ?? false,
        statistikk: naa?.statistikk ?? false,
      });
      setVelger(true);
      setVises(true);
    };
    // Svart et annet sted, for eksempel i innstillingene.
    const lagret = () => setVises(false);

    window.addEventListener(ENDRE, endre);
    window.addEventListener(LAGRET, lagret);
    return () => {
      window.removeEventListener(ENDRE, endre);
      window.removeEventListener(LAGRET, lagret);
    };
  }, []);

  const boksen = useRef<HTMLElement>(null);

  /**
   * Gir resten av sida beskjed om hvor høy beskjeden er.
   *
   * Målt og ikke gjettet: teksten brytes over ulikt antall linjer, og på en
   * smal skjerm legger knappene seg under teksten i stedet for ved siden av.
   * Et fast tall ville stemt på én bredde og bommet på alle andre.
   */
  useEffect(() => {
    const element = boksen.current;
    if (!vises || !element) return;

    const oppdater = () =>
      document.body.style.setProperty(
        '--kapselvarsel-hoyde',
        `${Math.ceil(element.getBoundingClientRect().height) + 32}px`,
      );
    oppdater();

    const maler = new ResizeObserver(oppdater);
    maler.observe(element);
    document.body.classList.add('har-kapselvarsel');

    return () => {
      maler.disconnect();
      document.body.classList.remove('har-kapselvarsel');
      document.body.style.removeProperty('--kapselvarsel-hoyde');
    };
  }, [vises]);

  if (!vises) return null;

  /**
   * Svaret lagres, og det som er lagret følger det.
   *
   * Et ja til annonsemåling skriver ned kampanjen som ligger i minnet, så den
   * overlever at fanen lukkes – det er nettopp det samtykket kjøper. Et ja til
   * besøksstatistikk melder landingen en gang til, nå med ID, så det første
   * besøket kommer med. Et nei fjerner det som måtte ligge der fra før, for
   * eksempel fra et ja som siden er trukket tilbake – og ber serveren slette
   * besøkene som hører til ID-en.
   */
  const svar = (valg: Valg) => {
    lagreValg(valg);
    if (valg.annonsemaaling) huskKampanje();
    else glemLagretKampanje();
    if (valg.statistikk) meldEtterSamtykke();
    else glemBesoksId(konto.current?.id ?? null);
    // Samme kall som innstillingene bruker: et nei tar klikk-ID-en på kontoen
    // med seg. Feiler det, står kontoen som før, og innstillingene viser det.
    if (konto.current && konto.current.annonsemaaling !== valg.annonsemaaling) {
      void api
        .patch('/gdpr/annonsemaaling', { samtykke: valg.annonsemaaling })
        .then(() => refresh())
        .catch(() => undefined);
    }
    setVises(false);
    setVelger(false);
  };

  return (
    <aside
      ref={boksen}
      className={`kapselvarsel${velger ? ' velger' : ''}`}
      role="region"
      aria-label="Om informasjonskapsler og måling"
    >
      <div className="kapselvarsel-tekst">
        <strong>Informasjonskapsler og måling</strong>
        <p>
          Vi bruker nødvendige informasjonskapsler for at siden skal fungere. Hvis du godtar det,
          bruker vi også en tilfeldig ID i nettleseren for å se hvor mange som kommer tilbake, og
          lagrer annonseklikk og deler informasjon om kjøp med Google eller Meta for å måle
          effekten av annonsene våre. Les mer i{' '}
          <Link to="/personvern">personvernerklæringen</Link>.
        </p>
        {velger ? (
          <div className="kapselvarsel-valg">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={valgt.statistikk}
                onChange={(event) => setValgt({ ...valgt, statistikk: event.target.checked })}
              />
              <span>
                <strong>Besøksstatistikk.</strong> En tilfeldig ID viser oss hvor mange som
                kommer tilbake, og hva som fører til kjøp. Når du logger inn, kobles den til
                kontoen din. Den deles ikke med andre.
              </span>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={valgt.annonsemaaling}
                onChange={(event) => setValgt({ ...valgt, annonsemaaling: event.target.checked })}
              />
              <span>
                <strong>Annonsemåling.</strong> Vi lagrer annonseklikk og deler informasjon om
                kjøp med Google eller Meta.
              </span>
            </label>
          </div>
        ) : (
          <button type="button" className="lenkeknapp kapselvarsel-velg" onClick={() => setVelger(true)}>
            Velg selv
          </button>
        )}
      </div>
      <div className="kapselvarsel-knapper">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => svar({ annonsemaaling: false, statistikk: false })}
        >
          Bare det nødvendige
        </button>
        {velger ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => svar(valgt)}>
            Lagre valget
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => svar({ annonsemaaling: true, statistikk: true })}
          >
            Greit
          </button>
        )}
      </div>
    </aside>
  );
}
